import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { useEntitlement } from './lib/entitlement';
import Upgrade, { EntitlementUnavailable } from './screens/Upgrade';
import Account from './screens/Account';
import CreateIdea from './screens/CreateIdea';
import EditIdea from './screens/EditIdea';
import Notes from './screens/Notes';
import Profile from './screens/Profile';
import SignIn from './screens/SignIn';

/**
 * The auth gate, then the Pro gate, then the routes behind both.
 *
 * Signed out, every path renders SignIn — including a deep link to `/idea/:id`, which is the
 * point: sign in and the note is there. The route is not redirected away, because losing the
 * destination would mean pasting the link again afterwards. The same holds for the Pro gate:
 * a free user following a note link lands on the upgrade screen with the route intact, so
 * subscribing takes them to the note rather than back to square one.
 *
 * This client is Pro-only, and the gate is one place on purpose. Note what it is and is not:
 * it decides what this app renders, not what the backend permits. Firestore rules grant every
 * authenticated user their own `users/{uid}/ideas`, and they must — free users on iOS and
 * Android read the same path through the same rules. So this is a product gate on a client,
 * not a security boundary around the data, and widening it in rules is not available without
 * breaking the phones. The Pro features that DO need enforcing (collaborative notes) check
 * entitlement server-side in the Cloud Functions, where a client cannot reach past it.
 */
export default function App() {
  const { user, loading } = useAuth();
  // Keyed on the uid, so switching accounts re-asks rather than inheriting a verdict.
  const { status, error, recheck } = useEntitlement(user?.uid);

  if (loading) return <main className="centre"><p className="muted">Loading…</p></main>;
  if (!user) return <SignIn />;

  if (status === 'checking') {
    return <main className="centre"><p className="muted">Checking your subscription…</p></main>;
  }
  // 'unavailable' is not 'free'. A check that could not reach an answer must not present a
  // subscriber with a paywall — see EntitlementUnavailable.
  if (status === 'unavailable') return <EntitlementUnavailable error={error} onRetry={recheck} />;
  if (status === 'free') return <Upgrade onRecheck={recheck} />;

  return (
    <Routes>
      <Route path="/" element={<Notes />} />
      <Route path="/new" element={<CreateIdea />} />
      <Route path="/idea/:id" element={<EditIdea />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/account" element={<Account />} />
      {/* Hosting rewrites every unknown path to index.html, so an unknown path arrives here
          rather than 404ing. Send it to the list instead of rendering nothing. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
